/**
 * Registry of the patient chats that are currently OPEN (visible) in this app.
 *
 * Several ChatModal instances can exist at once (the page-level chat opened from a
 * list / detail screen, plus one per ChatDock bubble), and each one keeps its own
 * socket. The global `new_message` handler (useSocketNotifications) and the ChatDock
 * use this registry to know whether the provider is already looking at the
 * conversation an incoming message belongs to, so they can deliver it into that open
 * chat instead of toasting / popping a dock bubble.
 *
 * A Map of entries (not a single "active room id") because more than one chat can be
 * open at the same time, and an entry can be registered before its room id resolves.
 */

const entries = new Map(); // token -> { roomId, appointmentId, patientUserId, claimRef, source }
const listeners = new Set();

const toId = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

// String conversation references (e.g. insurance claim ids such as "CLAIM-434567").
const toRef = (value) => {
  if (value == null) return null;
  const s = String(value).trim();
  return s || null;
};

const normalize = (info = {}) => ({
  roomId: toId(info.roomId),
  appointmentId: toId(info.appointmentId),
  patientUserId: toId(info.patientUserId),
  claimRef: toRef(info.claimRef),
  source: info.source || "page",
});

const notify = () => {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (e) {
      /* a listener failing must not break the others */
    }
  });
};

/**
 * Mark a chat as open. Returns a handle to update it (e.g. once the room id is known)
 * and to unregister it when the chat closes or unmounts.
 */
export function registerActiveChat(info) {
  const token = {};
  entries.set(token, normalize(info));
  notify();
  return {
    update(patch) {
      if (!entries.has(token)) return;
      entries.set(token, normalize({ ...entries.get(token), ...patch }));
      notify();
    },
    unregister() {
      if (entries.delete(token)) notify();
    },
  };
}

const matches = (entry, { roomId, appointmentId, claimRef, senderId } = {}) => {
  // A room id identifies the conversation exactly: when both sides know it, it decides.
  // (Same appointment but a different room is a different conversation — the open chat
  // would not render it, so it must still be notified through the dock.)
  const room = toId(roomId);
  if (room && entry.roomId) return room === entry.roomId;

  // Room not known on one side yet (the chat is still resolving it): fall back to the
  // conversation scope (claim / appointment) and make sure it's the same counterpart.
  const sender = toId(senderId);
  const sameCounterpart =
    !sender || !entry.patientUserId || sender === entry.patientUserId;

  const claim = toRef(claimRef);
  if (claim && entry.claimRef) return claim === entry.claimRef && sameCounterpart;

  const appt = toId(appointmentId);
  if (appt && entry.appointmentId)
    return appt === entry.appointmentId && sameCounterpart;

  return false;
};

/**
 * Whether a chat for the given room / claim / appointment (and sender) is open.
 * `excludeSource` ignores entries registered by that source (ChatDock uses it so its
 * own expanded bubble does not count as "open elsewhere").
 */
export function isChatOpen(query = {}, { excludeSource } = {}) {
  for (const entry of entries.values()) {
    if (excludeSource && entry.source === excludeSource) continue;
    if (matches(entry, query)) return true;
  }
  return false;
}

/** Subscribe to open/close changes. Returns the unsubscribe function. */
export function subscribeActiveChats(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Window event used to hand a message received by the global socket to the open chat. */
export const OPEN_CHAT_MESSAGE_EVENT = "chat-open-room-message";

/** Normalize ids for comparisons (socket payloads may carry numbers or strings). */
export const sameId = (a, b) =>
  a != null && b != null && String(a) === String(b);
